import { Navigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtBRL, fmtNum, fmtDate, maskCnpjCpf, maskName } from "@/lib/format";
import type { NotaSimplificada, NotaStatus } from "@/lib/fiscal-types";
import { X, Search, FileText, Filter, ArrowLeft, ArrowRight, ArrowDownUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NotaDetailModal } from "@/components/NotaDetailModal";
import { StatusBadge } from "@/components/StatusBadge";

const PAGE = 50;

const STATUS_LABEL: Record<NotaStatus, string> = {
  AUTORIZADA: "Autorizadas",
  CONTINGENCIA: "Autorizada em Contingência",
  INUTILIZADA: "Inutilizadas",
  CANCELADA: "Canceladas",
};

export default function NotasPage() {
  const { notas, dashboard, filtroStatus, setFiltroStatus, privacyMode } = useFiscalStore();
  const [q, setQ] = useState("");
  const [modelo, setModelo] = useState<"all" | "55" | "65">("all");
  const [page, setPage] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  const [sortKey, setSortKey] = useState<"numero" | "emissao" | "valorOficial" | "qtdItens">("emissao");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => { document.title = "Notas Fiscais — Extrator Fiscal"; }, []);
  useEffect(() => { setPage(0); }, [filtroStatus]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const arr = notas.filter((n) => {
      if (filtroStatus && n.notaStatus !== filtroStatus) return false;
      if (modelo !== "all" && String(n.modelo) !== modelo) return false;
      if (!ql) return true;
      return (
        n.chave.toLowerCase().includes(ql) ||
        n.numero.toLowerCase().includes(ql) ||
        n.emitente.toLowerCase().includes(ql) ||
        n.cnpjEmit.toLowerCase().includes(ql)
      );
    });

    return arr.sort((a, b) => {
      const factor = sortDir === "asc" ? 1 : -1;
      if (sortKey === "numero") return (parseInt(a.numero) - parseInt(b.numero)) * factor;
      if (sortKey === "emissao") return ((a.emissao || "").localeCompare(b.emissao || "")) * factor;
      if (sortKey === "valorOficial") return (a.valorOficial - b.valorOficial) * factor;
      if (sortKey === "qtdItens") return (a.qtdItens - b.qtdItens) * factor;
      return 0;
    });
  }, [notas, q, modelo, filtroStatus, sortKey, sortDir]);

  if (!dashboard) return <Navigate to="/upload" replace />;

  const slice = filtered.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  return (
    <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1600px] mx-auto space-y-6 lg:space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-3xl font-extrabold tracking-tight dark:text-white">Notas Fiscais</h1>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1">
            Explorador detalhado de {fmtNum(filtered.length)} documentos fiscais.
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl w-fit">
          <FileText className="size-4 text-blue-600 dark:text-blue-400" />
          <span className="text-xs lg:text-sm font-medium text-blue-900 dark:text-blue-200">Analisado: {fmtNum(notas.length)}</span>
        </div>
      </header>

      {/* Controles de Busca e Filtro em Painel Glass */}
      <div className="glass-card p-3 lg:p-4 flex flex-col xl:flex-row items-center gap-4">
        <div className="relative flex-1 w-full">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input 
            placeholder="Buscar por chave, número, emitente..." 
            value={q} 
            onChange={(e) => { setQ(e.target.value); setPage(0); }} 
            className="w-full pl-10 pr-4 py-2 lg:py-2.5 rounded-xl text-xs lg:text-sm bg-white/5 border border-slate-200 dark:border-white/10 outline-none focus:ring-2 focus:ring-primary/50 transition-all dark:text-white glass"
          />
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto">
          <Select
            value={filtroStatus ?? "all"}
            onValueChange={(v) => setFiltroStatus(v === "all" ? null : (v as NotaStatus))}
          >
            <SelectTrigger className="w-full sm:w-[180px] bg-white/5 border-slate-200 dark:border-white/10 glass rounded-xl">
              <SelectValue placeholder="Filtrar Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              <SelectItem value="AUTORIZADA">Autorizadas</SelectItem>
              <SelectItem value="CONTINGENCIA">Autorizada em Contingência</SelectItem>
              <SelectItem value="INUTILIZADA">Inutilizadas</SelectItem>
              <SelectItem value="CANCELADA">Canceladas</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex w-full sm:w-auto rounded-xl border border-slate-200 dark:border-white/10 bg-white/5 glass p-0.5">
            {(["all", "55", "65"] as const).map((m) => (
              <button
                key={m}
                onClick={() => { setModelo(m); setPage(0); }}
                className={`flex-1 sm:flex-none px-3 lg:px-4 py-2 text-[10px] lg:text-xs font-bold rounded-lg transition-colors ${modelo === m ? "bg-primary text-white shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"}`}
              >
                {m === "all" ? "Todas" : m === "55" ? "NF-e" : "NFC-e"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filtroStatus && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-2 lg:py-3 animate-in slide-in-from-top-2">
          <div className="text-[11px] lg:text-sm text-primary flex items-center gap-2">
            <Filter className="size-4" />
            Filtro: <span className="font-extrabold uppercase">{STATUS_LABEL[filtroStatus]}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setFiltroStatus(null)} className="h-7 lg:h-9 gap-1.5 text-primary hover:bg-primary/20 hover:text-primary rounded-lg font-bold text-[10px] lg:text-xs">
            <X className="size-4" /> Limpar
          </Button>
        </div>
      )}

      {/* Tabela Glass */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] lg:text-sm whitespace-nowrap">
            <thead className="bg-slate-50/5 dark:bg-slate-900/50">
              <tr className="text-muted-foreground border-b border-slate-200 dark:border-white/10">
                <th className="hidden lg:table-cell text-left p-4 font-bold text-[10px] uppercase">Mod.</th>
                <th className="text-left p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSort("numero")}>
                  <div className="flex items-center gap-1">Nº / Série <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th className="text-left p-4 font-bold text-[10px] uppercase">Emitente</th>
                <th className="text-left p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSort("emissao")}>
                  <div className="flex items-center gap-1">Emissão <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th className="hidden sm:table-cell text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSort("qtdItens")}>
                  <div className="flex items-center justify-end gap-1">Itens <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSort("valorOficial")}>
                  <div className="flex items-center justify-end gap-1">Valor Oficial <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th className="text-left p-4 font-bold text-[10px] uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/5">
              {slice.map((n, idx) => (
                <Row key={n.chave + n.numero} n={n} onClick={() => setOpenIndex(page * PAGE + idx)} privacyMode={privacyMode} />
              ))}
              {slice.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Search className="size-8 opacity-20" />
                      <span>Nenhuma nota encontrada com os filtros atuais.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Paginação */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t border-slate-200 dark:border-white/10 text-xs bg-slate-50/50 dark:bg-transparent gap-4">
          <span className="font-medium text-slate-500 dark:text-slate-400">
            Exibindo <span className="font-bold text-slate-700 dark:text-white">{(page * PAGE) + 1} - {Math.min((page + 1) * PAGE, filtered.length)}</span> de {fmtNum(filtered.length)} registros
          </span>
          <div className="flex items-center gap-4">
            <span className="text-[10px] text-muted-foreground">Página {page + 1} de {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="p-2 rounded-lg border border-slate-200 dark:border-white/10 glass disabled:opacity-30 hover:bg-primary/10">
                <ArrowLeft className="size-4" />
              </button>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="p-2 rounded-lg border border-slate-200 dark:border-white/10 glass disabled:opacity-30 hover:bg-primary/10">
                <ArrowRight className="size-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <NotaDetailModal
        notas={filtered}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onChangeIndex={setOpenIndex}
      />
    </div>
  );
}

function Row({ n, onClick, privacyMode }: { n: NotaSimplificada; onClick: () => void; privacyMode?: boolean }) {
  return (
    <tr className="hover:bg-primary/5 transition-colors cursor-pointer group" onClick={onClick}>
      <td className="hidden lg:table-cell p-4">
        <span className={`px-2 py-1 text-[9px] font-black rounded border ${n.modelo === 55 ? "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:bg-blue-500/20 dark:text-blue-300" : "bg-purple-500/10 text-purple-600 border-purple-500/20 dark:bg-purple-500/20 dark:text-purple-300"}`}>
          {n.modelo}
        </span>
      </td>
      <td className="p-4">
        <div className="font-bold text-slate-800 dark:text-white tabular-nums text-xs lg:text-sm">{n.numero}/{n.serie}</div>
      </td>
      <td className="p-4">
        <div className="font-medium text-slate-700 dark:text-slate-300 max-w-[140px] lg:max-w-[280px] truncate" title={n.emitente}>
          {n.emitente ? (privacyMode ? maskName(n.emitente) : n.emitente) : "—"}
        </div>
        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
          {privacyMode ? maskCnpjCpf(n.cnpjEmit) : n.cnpjEmit}
        </div>
      </td>
      <td className="p-4 text-[11px] lg:text-xs text-slate-600 dark:text-slate-400 tabular-nums">
        {fmtDate(n.emissao)}
      </td>
      <td className="hidden sm:table-cell p-4 text-right tabular-nums text-muted-foreground">{fmtNum(n.qtdItens)}</td>
      <td className="p-4 text-right tabular-nums font-black text-emerald-600 dark:text-emerald-400">{fmtBRL(n.valorOficial)}</td>
      <td className="p-4">
        <StatusBadge status={n.notaStatus} size="sm" />
      </td>
    </tr>
  );
}
